// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// streams.ts — five INDEPENDENT random streams, and the reason there are five rather than one.
//
// One shared PRNG is the obvious design and it quietly destroys the balance sim's most valuable
// tool. The mechanism ablations (principle #21) work by disabling ONE mechanic and demanding that
// everything unrelated comes back BYTE-IDENTICAL — that identity is the positive control proving the
// ablation flag is wired to the real mechanic and not to a shadow of it. With a single stream,
// turning illness off removes the per-hour illness rolls from the sequence and shifts every
// subsequent weather draw, forage variance and arrival roll, so the control arm is guaranteed to
// fail for a reason that has nothing to do with the mechanic. The sibling failure — a control arm
// that comes back byte-identical because the mechanic was never actually disabled — is recorded in
// this factory's notes as "sim control arm must stay disabled"; five streams make BOTH diagnosable,
// because identical-except-illness is now a fact the test can assert rather than hope for.
//
// The second thing they buy is smaller and more immediate: A PLAYER ACTION CAN NEVER SHIFT THE
// WEATHER. With one stream, designating a dig on tick 400 consumes a draw and the blizzard on day 7
// lands differently — which is not a bug anybody would ever find, and would make the forecast a lie.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';

/**
 * The streams. Adding one is a wire change: a new name must be APPENDED, never inserted, or every
 * existing seed produces a different winter and every recorded run stops reproducing.
 */
export const STREAM_NAMES = ['weather', 'illness', 'forage', 'names', 'arrivals'] as const;
export type StreamName = (typeof STREAM_NAMES)[number];

export type Streams = Record<StreamName, Rng>;

/**
 * Derive the five from one run seed. Each stream's own seed is the run seed mixed with the stream's
 * INDEX, not with a hash of its name — an index is stable under a rename and is trivially auditable,
 * and the multiplier is a large odd constant so two adjacent run seeds do not produce two streams
 * that walk in step.
 */
export function makeStreams(seed: number): Streams {
  const out = {} as Streams;
  for (let i = 0; i < STREAM_NAMES.length; i++) {
    out[STREAM_NAMES[i]] = makeRng((seed ^ ((i + 1) * 0x9e3779b1)) >>> 0);
  }
  return out;
}

/**
 * An integer in [0, n). Written here rather than taken from the engine's randInt because the whole
 * simulation is integer arithmetic and this is the only place a float is allowed to touch it: one
 * multiply and one floor, at the boundary.
 */
export function below(rng: Rng, n: number): number {
  return Math.min(n - 1, (rng() * n) | 0);
}

/** A percentage roll: true with probability `pct`/100. */
export function chance(rng: Rng, pct: number): boolean {
  return below(rng, 100) < pct;
}
